import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, KeyValuePair, IdentifierType, SeriesSelectorViewModel, SelectorType, lineStyles, IValueViewModel } from '../../models';
import { ISeriesSelectorService, ISeriesService, IIdentifierVmService } from '../../services';
import { Helper, wtDroppableDrop, seriesAdded, groupIdentifierSeparator } from '../../common';
import { SeriesConfiguration } from '../../widgets';

class SeriesTableController implements angular.IComponentController {
    identifierTypes: KeyValuePair<string, IdentifierType>[];
    isBindable: boolean;
    isLoading: boolean;
    lineStyles: string[];
    loadingMessage: string;
    selectedSerie: any;
    series: any[];
    seriesSelectorViewModels: { [guid: string]: SeriesSelectorViewModel };
    sortConfig: {
        animation: number;
        onSort: (e: any) => void;
        handle: string;
    };
    symbols: string[];
    tableFormController: angular.IFormController;
    tuples: any[];

    onAdd?: (series: any) => void;
    onRemove?: (series: any) => void;

    readonly checkDirty: boolean;
    readonly disableHideColumn: boolean;
    readonly extraSelectorViewModels: { [guid: string]: SeriesSelectorViewModel };
    readonly extraViewModels: { [guid: string]: IValueViewModel };
    readonly hasOffset: boolean;
    readonly id: string;
    readonly type: string;

    private garbages: (() => void)[] = [];

    static $inject = [
        '$scope',
        'seriesSelectorService',
        'seriesService',
        'identifierVmService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private seriesSelectorService: ISeriesSelectorService,
        private _seriesService: ISeriesService,
        private _identifierVmService: IIdentifierVmService
    ) {
    }

    $onInit() {
        this.lineStyles = lineStyles;
        this.sortConfig = {
            animation: 150,
            handle: '.drag-handle',
            onSort: evt => this.sort(evt)
        };
        this.symbols = Helper.getSymbols();

        this.garbages.push(this._$scope.$on(wtDroppableDrop, (event, data) => {
            if (data.itemId) {
                const guid = data.itemId.replace('id', '');
                if (data.binId) {
                    const bin = data.binId.split('+');
                    this.updateSerie(bin[0], bin[1], guid);
                } else {
                    this.addSeries(guid);
                }
            }
        }));

        this.garbages.push(this._$scope.$on(seriesAdded, (event, data) => {
            this.addSvmSeries(data);
        }));

        this.garbages.push(this._$scope.$watch(
            () => this.series,
            newValue => {
                if (newValue && (!this.tableFormController || this.tableFormController.$valid)) {
                    this.mapSeries(newValue);
                }
            },
            true)
        );

        this.seriesSelectorService
            .getSeriesSelectorViewModels()
            .then(response => {
                const dictionnary = {};
                response.forEach(r => dictionnary[r.guid] = r);
                this.seriesSelectorViewModels = dictionnary;
                this.identifierTypes = response.map(svm => {
                    return {
                        key: svm.guid,
                        value: svm.identifierType
                    };
                });
            });
    }

    $onDestroy() {
        this.garbages.forEach(g => g());
    }

    private mapSeries(series: any[]) {
        this._identifierVmService
            .getViewModelsFromApiFromGuidList(series.map(d => d.guid))
            .then(data => {
                this.tuples = series.map(d => {
                    let viewModel = _.find(data, dd => dd.guid === d.guid);
                    if (!viewModel && this.extraViewModels) {
                        viewModel = this.extraViewModels[d.guid];
                    }
                    if (viewModel === undefined) {
                        return {
                            name: '',
                            viewModel: null,
                            series: d
                        };
                    } else {
                        return {
                            name: viewModel.groupUniqueName
                                ? viewModel.groupUniqueName + groupIdentifierSeparator + viewModel.name
                                : viewModel.name,
                            viewModel: viewModel,
                            series: d
                        };
                    }
                });
                this.tuples = this.tuples.filter(s => !!s);
                this.tuples = this.tuples.sort((a, b) => a.series.sortOrder - b.series.sortOrder);
            });
    }

    private sort(evt) {
        evt.models.forEach((s, i) => s.series.sortOrder = i);
        this.series = evt.models.map(d => d.series);
    }


    select(serie): void {
        this.selectedSerie = serie;
    }

    canBind(series: SeriesSelectorViewModel) {
        return this.isBindable && series && series.type === SelectorType.identifier;
    }

    remove(series: SeriesConfiguration): void {
        _.remove(this.series, s => s === series);
        if (this.onRemove) {
            this.onRemove({ series });
        }
        this.mapSeries(this.series);
    }

    setXAxis(guid: string, value: string): void {
        this.series.forEach(s => {
            if (s.guid !== guid || s.value !== value) {
                s.isXAxis = false;
            } else {
                s.isXAxis = true;
            }
        });
    }

    setSymbol(series: any, newSymbol: string): void {
        series.symbol = newSymbol;
    }

    private updateSerie(oldValue: string, oldGuid: string, newGuid: string) {
        const svm = this.seriesSelectorViewModels[newGuid];
        const old = _.find(this.series, s => s.guid === oldGuid && s.value === oldValue);
        const newValueType = this.seriesSelectorService.getAvailableValueType(svm, this.series, old);

        if (newValueType) {
            old.guid = newGuid;
            old.value = newValueType;
            old.type = svm.type;
            old.isBoundToGroup = svm.type === SelectorType.identifier
                ? old.isBoundToGroup
                : false;

            if (this.onAdd) {
                this.onAdd({ series: old });
            }
        }
    }

    private addSeries(guid: string): void {
        let svm = this.seriesSelectorViewModels[guid];
        if (!svm && !!this.extraSelectorViewModels) {
            svm = this.extraSelectorViewModels[guid];
        }
        return this.addSvmSeries(svm);
    }

    private addSvmSeries(svm: SeriesSelectorViewModel): void {
        if (!this.identifierTypes) {
            this.identifierTypes = [];
        }

        const length = this.series.length;
        const serie = new SeriesConfiguration(
            svm.guid,
            Helper.getColor(length),
            Helper.getSymbol(length)
        );

        serie.type = svm.type;
        if (this.type === 'scatter') {
            serie.isSymbolVisible = true;
        }
        if (!!svm && !!svm.rawInformation) {
            serie.rawInformation = svm.rawInformation;
        }

        const newValueType = this.seriesSelectorService.getAvailableValueType(svm, this.series);
        if (newValueType) {
            serie.value = newValueType;
            if (this.series.length) {
                let max = Math.max(...this.series.map(s => s.sortOrder as number));
                if (!max) {
                    max = this.series.length;
                }
                serie.sortOrder = max;
            } else {
                serie.sortOrder = 0;
            }
            this._seriesService.bind(serie, svm.groupAbsolutePath);
            this.series.push(serie);
            this.mapSeries(this.series);
            if (this.onAdd) {
                this.onAdd({ series: serie });
            }
        }
    }
}

export const wtSeriesTable: ComponentDefinition = {
    componentId: 'wtSeriesTable',
    controller: SeriesTableController,
    template: require('./seriesTable.component.html'),
    controllerAs: 'vm',
    bindings: {
        checkDirty: '<?',
        extraSelectorViewModels: '<?',
        extraViewModels: '<?',
        disableHideColumn: '<?',
        formController: '=?',
        hasOffset: '<?',
        id: '@',
        isBindable: '=',
        onAdd: '&?',
        onRemove: '&?',
        series: '=',
        type: '@',
    }
};